import React, { useState } from 'react';
import {
  Tag,
} from 'antd';
import {
  CloseOutlined,
  MailOutlined,
  MessageOutlined,
  SettingOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Tab, useTabStore } from '../../store/tabs';
import { SaveStateDialog } from '../Dialogs';
import { apiService } from '../../services/api';
import { Workspace } from '../../types';

interface TabBarProps {
  className?: string;
}

export const TabBar: React.FC<TabBarProps> = ({ className }) => {
  const { tabs, activeTabId, isInitialized, setActiveTab, requestCloseTab, forceCloseTab, saveTabState } = useTabStore();
  
  const [saveDialog, setSaveDialog] = useState<{
    isOpen: boolean;
    tabId: string;
    tabTitle: string;
    workspace?: Workspace;
    existingName?: string;
    existingAutoOpen?: boolean;
    isManualSave?: boolean; // Track if this is a manual save vs close save
  }>({
    isOpen: false,
    tabId: '',
    tabTitle: '',
    workspace: undefined,
    existingName: undefined,
    existingAutoOpen: undefined,
    isManualSave: false,
  });

  const getTabIcon = (tab: Tab) => {
    switch (tab.type) {
      case 'email':
        return <MailOutlined style={{ fontSize: '16px' }} />;
      case 'settings':
        return <SettingOutlined style={{ fontSize: '16px' }} />;
      case 'workspace':
        return <MessageOutlined style={{ fontSize: '16px' }} />;
      default:
        return null;
    }
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  const getExistingSavedStateInfo = async (tab: Tab): Promise<{name?: string, autoOpen?: boolean}> => {
    // Only return existing info if this tab is already linked to a saved state
    if (!tab.savedStateId) {
      return {};
    }
    
    try {
      const savedState = await apiService.getSavedState(tab.savedStateId);
      return {
        name: savedState.name,
        autoOpen: savedState.autoOpenOnStartup
      };
    } catch (error) {
      console.error('Failed to fetch existing saved state:', error);
      // Fallback to tab title if API call fails
      return {
        name: tab.title,
        autoOpen: undefined
      };
    }
  };

  const handleCloseTab = async (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    
    const result = requestCloseTab(tabId);
    
    if (result.needsSave && result.tab) {
      // Show save dialog
      const existingInfo = await getExistingSavedStateInfo(result.tab);
      setSaveDialog({
        isOpen: true,
        tabId: tabId,
        tabTitle: result.tab.title,
        workspace: result.tab.workspace,
        existingName: existingInfo.name,
        existingAutoOpen: existingInfo.autoOpen,
        isManualSave: false, // This is a close-triggered save
      });
    }
    // If doesn't need saving, tab is already closed by requestCloseTab
  };

  const handleSaveTab = async (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;
    
    // Show save dialog for manual save
    const existingInfo = await getExistingSavedStateInfo(tab);
    setSaveDialog({
      isOpen: true,
      tabId: tabId,
      tabTitle: tab.title,
      workspace: tab.workspace,
      existingName: existingInfo.name,
      existingAutoOpen: existingInfo.autoOpen,
      isManualSave: true,
    });
  };

  const handleSaveConfirm = async (name: string, autoOpenOnStartup?: boolean) => {
    if (saveDialog.tabId) {
      try {
        await saveTabState(saveDialog.tabId, name, autoOpenOnStartup);
        
        // Only close tab if this was triggered by tab close, not manual save
        if (!saveDialog.isManualSave) {
          forceCloseTab(saveDialog.tabId);
        }
      } catch (error) {
        console.error('Failed to save tab state:', error);
        // Could show error notification here
      }
    }
    setSaveDialog({ isOpen: false, tabId: '', tabTitle: '', workspace: undefined, existingName: undefined, existingAutoOpen: undefined, isManualSave: false });
  };

  const handleSaveCancel = () => {
    if (saveDialog.tabId && !saveDialog.isManualSave) {
      // Only force close if this was a close-triggered save dialog
      forceCloseTab(saveDialog.tabId);
    }
    setSaveDialog({ isOpen: false, tabId: '', tabTitle: '', workspace: undefined, existingName: undefined, existingAutoOpen: undefined, isManualSave: false });
  };

  // Always render the tab bar container, even if no tabs
  if (tabs.length === 0) {
    return (
      <div className={`tab-bar empty ${className || ''}`} style={{
        height: '40px',
        borderBottom: '1px solid var(--color-border-secondary)',
        backgroundColor: 'var(--color-bg-primary)', // White background for tab area
        display: 'flex',
        alignItems: 'center',
        padding: '0 1rem',
        fontSize: '0.875rem',
        color: 'var(--color-text-primary)', // More visible text
        boxShadow: 'var(--shadow-light)', // Add shadow for visibility
      }}>
        {!isInitialized ? 'Restoring saved workspaces...' : ''}
      </div>
    );
  }

  return (
    <div className={`tab-bar ${className || ''}`} style={{
      display: 'flex',
      minHeight: '40px',
      borderBottom: '1px solid var(--color-border-secondary)',
      backgroundColor: 'var(--color-bg-primary)', // White background for tab area
      boxShadow: 'var(--shadow-light)', // Add shadow for visibility
      overflowX: 'auto',
      scrollbarWidth: 'thin',
    }}>
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;
        
        return (
          <div
            key={tab.id}
            className={`tab ${isActive ? 'active' : ''}`}
            onClick={() => handleTabClick(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 'fit-content',
              maxWidth: '200px',
              padding: '0.5rem 0.75rem',
              cursor: 'pointer',
              borderBottom: isActive ? '2px solid var(--color-border-focus)' : '2px solid transparent',
              backgroundColor: isActive ? 'var(--color-bg-secondary)' : 'transparent',
              transition: 'all 0.2s ease',
              position: 'relative',
              borderRight: '1px solid var(--color-border-secondary)',
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            {/* Tab Icon */}
            <div style={{ marginRight: '0.5rem', flexShrink: 0 }}>
              {getTabIcon(tab)}
            </div>

            {/* Tab Title */}
            <span style={{
              fontSize: '0.875rem',
              fontWeight: isActive ? '500' : '400',
              color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}>
              {tab.title}
            </span>

            {/* Tab Actions */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.25rem',
              marginLeft: '0.5rem',
              flexShrink: 0,
            }}>
              {/* Save button for workspace tabs */}
              {tab.type === 'workspace' && (
                <button
                  onClick={(e) => handleSaveTab(e, tab.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '2px',
                    cursor: 'pointer',
                    color: 'var(--color-text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.7,
                    transition: 'opacity 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.color = 'var(--color-text-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.7';
                    e.currentTarget.style.color = 'var(--color-text-tertiary)';
                  }}
                  title="Save current state"
                >
                  <SaveOutlined style={{ fontSize: '14px' }} />
                </button>
              )}

              {/* Close button */}
              <button
                onClick={(e) => handleCloseTab(e, tab.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '2px',
                  cursor: 'pointer',
                  color: 'var(--color-text-tertiary)',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: 0.7,
                  transition: 'opacity 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.color = 'var(--color-text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.7';
                  e.currentTarget.style.color = 'var(--color-text-tertiary)';
                }}
                title="Close tab"
              >
                <CloseOutlined style={{ fontSize: '14px' }} />
              </button>
            </div>

            {/* Saved state indicator */}
            {tab.savedStateId && (
              <div style={{
                position: 'absolute',
                top: '2px',
                right: '2px',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-primary)',
              }} />
            )}
          </div>
        );
      })}
      
      {/* Save State Dialog */}
      <SaveStateDialog
        isOpen={saveDialog.isOpen}
        tabTitle={saveDialog.tabTitle}
        workspace={saveDialog.workspace}
        existingName={saveDialog.existingName}
        existingAutoOpen={saveDialog.existingAutoOpen}
        isManualSave={saveDialog.isManualSave}
        onConfirm={handleSaveConfirm}
        onCancel={handleSaveCancel}
      />
    </div>
  );
};